import TextInput from "../../../../shared/components/TextInput/TextInput";
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";
import {
    Box,
    Button,
    Flex,
    Heading,
    Link,
    Text,
    Stack,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import FormErrorResetEffect from "./FormErrorResetEffect";
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";

const LoginPage = () => {
    const { login, user, error, errorCode, resetError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation("default");

    useEffect(() => {
        return () => {
            resetError();
        };
    }, []);

    if (user) {
        return <Navigate to={"/"} replace />
    }

    const handleSubmit = (values, { setSubmitting }) => {
        const from = location.state?.from;
        login(values.email, values.password, from ? `${from.pathname}${from.search || ''}` : "/");
        setSubmitting(false);
    }

    const handleNavigate = (route) => {
        navigate(route);
    }

    const validationSchema = Yup.object({
        email: Yup.string()
            .email(t('form.email.message'))
            .required(t('form.email.required')),
        password: Yup.string()
            .min(8, t('form.password.length'))
            .required(t('form.password.required')),
    });

    return (
        <Flex
            bg="surface.page"
            w="100%" minH={{ base: 'calc(100vh - 64px)', md: 'calc(100vh - 72px)' }}
            align="center" justify="center"
            px={{ base: 4, sm: 6 }} py={{ base: '32px', md: '64px' }}>

            <Box w="full" maxW="480px" bg="surface.card" border="1px solid" borderColor="surface.border" borderRadius="22px"
                boxShadow="raised" p={{ base: '24px', md: '40px' }} display="flex" gap={'28px'} flexDirection={"column"}>
                <Stack spacing={2}>
                    <Heading as="h1" fontWeight={700} fontSize={{ base: '28px', md: '32px' }} letterSpacing="-0.6px">
                        {t('login.title')}
                    </Heading>
                    <Text fontSize={{ base: '15px', md: '16px' }} color="ink.500" lineHeight="1.5">
                        {t('login.message')}
                    </Text>
                    {location.state?.reason === 'session-expired' && (
                        <Text fontSize="14px" color="orange.700" bg="orange.50" borderRadius="10px" px="12px" py="8px">
                            {t('login.session_expired', 'Sesja wygasła — zaloguj się ponownie, aby kontynuować.')}
                        </Text>
                    )}
                </Stack>

                <Box w="full">
                    <Formik
                        initialValues={{ email: '', password: '' }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnChange={true}
                        validateOnBlur={true}
                    >
                        {({ isSubmitting, errors, touched, values, isValid, dirty }) => (
                            <Form>
                                <FormErrorResetEffect resetError={resetError} />
                                <Flex flexDirection={"column"} gap={'16px'}>
                                    <InlineErrorInput
                                        label={t('form.email.label')}
                                        name="email"
                                        type="email"
                                        placeholder={t('form.email.placeholder')}
                                        value={values.email}
                                        errors={errors.email}
                                        touched={touched.email}
                                        backgroundColor="white"
                                    />
                                    <Flex flexDirection={"column"} gap={'4px'}>
                                        <InlineErrorInput
                                            label={t('form.password.label')}
                                            name="password"
                                            type="password"
                                            placeholder={t('form.password.placeholder')}
                                            value={values.password}
                                            errors={errors.password}
                                            touched={touched.password}
                                            backgroundColor="white"
                                        />

                                        <Box textAlign="right">
                                            <Link
                                                className="login-forgot"
                                                href="/reset-password"
                                                color="#7A1F35"
                                                _hover={{ textDecoration: "underline" }}
                                            >
                                                {t('login.forgot')}
                                            </Link>
                                        </Box>
                                    </Flex>
                                </Flex>

                                {errorCode && (
                                    <Text color="red.500" textAlign="center" mt={4}>
                                        {t(errorCode)}
                                    </Text>
                                )
                                    ||
                                    error && (
                                        <Text color="red.500" textAlign="center" mt={4}>
                                            {t(error)}
                                        </Text>
                                    )}

                                <Box mt={6}>
                                    <Button
                                        type="submit"
                                        isLoading={isSubmitting && !error}
                                        bg="brand.500"
                                        color="white"
                                        _hover={{ bg: "brand.600" }}
                                        h={'52px'}
                                        w={'100%'}
                                        borderRadius={'14px'}
                                        fontSize={'16px'}
                                        fontWeight={700}
                                        isDisabled={isSubmitting || !isValid || !dirty}
                                    >
                                        {t('login.button')}
                                    </Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
                <Text fontSize="15px" color="ink.600" textAlign="center" pt="20px" borderTop="1px solid" borderColor="surface.border">
                    {t('login.register.message')}{' '}
                    <Link href="/register" color="brand.500" fontWeight={700}>
                        {t('login.register.button')}
                    </Link>
                </Text>
            </Box>
        </Flex>
    )
}

export default LoginPage;
